// CSRF double-submit helper for the web client.
//
// The API sets two cookies on login / refresh:
//   - cshub_rt        (httpOnly; JS can't read it — that's the point)
//   - cshub_csrf      (NOT httpOnly — JS reads this and echoes in a header)
//
// In production the cookies are prefixed `__Secure-` so browsers refuse to
// forward them over non-HTTPS, and we set `Domain=.bytego.ai` so the same
// session (refresh + CSRF cookies) is shared across every school subdomain.
// We accept the legacy `__Host-` name too in case a stale cookie from an
// older deploy is still on a tester's machine.
//
// This util is framework-agnostic. Both axios clients (`lib/api.ts` and
// `api/client.ts`) share it via an interceptor.

const COOKIE_CANDIDATES = [
  "__Secure-cshub_csrf",
  "__Host-cshub_csrf",
  "cshub_csrf",
];

export function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";").map((c) => c.trim());
  for (const entry of cookies) {
    const eq = entry.indexOf("=");
    if (eq < 0) continue;
    const name = entry.slice(0, eq);
    const value = entry.slice(eq + 1);
    if (COOKIE_CANDIDATES.includes(name) && value) {
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }
  return null;
}

export const CSRF_HEADER = "X-CSRF-Token";

// Methods that DO NOT need a CSRF token. Keep aligned with the API-side
// allowlist in `apps/api/src/plugins/csrf.ts`.
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export function needsCsrf(method: string | undefined): boolean {
  if (!method) return false;
  return !SAFE_METHODS.has(method.toUpperCase());
}
